import { useState } from "react"
import { Star } from "lucide-react"
import { cn } from "@/lib/utils"

interface StarRatingProps {
  rating: number
  onRatingChange: (rating: number) => void
  size?: "sm" | "md" | "lg"
  disabled?: boolean
}

export const StarRating = ({ 
  rating, 
  onRatingChange, 
  size = "md", 
  disabled = false 
}: StarRatingProps) => {
  const [hoveredRating, setHoveredRating] = useState(0)

  const sizeClasses = {
    sm: "w-5 h-5",
    md: "w-8 h-8",
    lg: "w-10 h-10"
  }

  const handleStarClick = (starRating: number) => {
    if (disabled) return
    onRatingChange(starRating)
  }

  const handleStarHover = (starRating: number) => {
    if (disabled) return
    setHoveredRating(starRating)
  }

  const handleMouseLeave = () => {
    if (disabled) return
    setHoveredRating(0)
  }

  const getStarColor = (starIndex: number) => {
    const currentRating = hoveredRating || rating
    if (starIndex <= currentRating) {
      return "text-yellow-400 fill-yellow-400"
    }
    return "text-muted-foreground hover:text-yellow-400"
  }

  return (
    <div className="flex gap-1" onMouseLeave={handleMouseLeave}>
      {[1, 2, 3, 4, 5].map((starIndex) => (
        <button
          key={starIndex}
          type="button"
          onClick={() => handleStarClick(starIndex)}
          onMouseEnter={() => handleStarHover(starIndex)}
          disabled={disabled}
          className={cn(
            "transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded",
            disabled && "cursor-not-allowed opacity-50"
          )}
        >
          <Star 
            className={cn(
              sizeClasses[size], 
              "transition-colors duration-200",
              getStarColor(starIndex)
            )} 
          />
        </button>
      ))}
    </div>
  )
}